import { notification } from 'antd';
import { Toolbar } from '@mui/material';
import React, { useEffect, useState } from 'react';
import { EditProps, Edit, SaveButton, DeleteButton, SimpleForm } from 'react-admin';

import { UserInfo } from './UserInfo';
import { axiosErrorToString } from '@utils';
import { UserInitial } from '@pages/user/UserInitial';
import {
  CreditSurveyTableColProps,
  CreditSurveyTableRowProps,
  IProfileProgress,
  UpdateCreditSurveyType,
  surveyHttp,
  userHttp,
} from '@network';
import { UserOrdersList } from '@pages/user/UserOrders';
import { UserFarmsPage } from '@pages/user/UserFarmsPage';
import { UserHistoryPage } from '@pages/user/UserHistoryPage';
import { UserSurveyDetail } from '@pages/user/UserSurveyDetail';
import { UserPassword } from '@pages/user/UserEdit/UserPassword';
import { UserComments } from '@pages/user/UserEdit/UserComments';
import { AnswerCol, AnswerRow, SurveyQuestion, User } from '@types';
import { UserSalesforcePage } from '@pages/user/UserSalesforcePage';
import { UserCreditBureauPage } from '@pages/user/UserCreditBureauPage';
import { UserAddresses, UserAddressesList } from '@pages/user/UserAddresses';
import { Forbidden, Loader, InitialPage, ReminderComponent } from '@components';
import { useEditProps, useQuery, useRbacEdit, useRbacView, useRecord } from '@hooks';
import cls from './UserEdit.module.css';


const Title = ({ record }: {record?: User}) =>
  record ? (<span>User: {record.fullName}</span>) : null;

export const UserEdit = (props: EditProps) => {
  const user = useRecord<User>(props);
  const record: User = useRecord(props);
  const editProps = useEditProps();
  const rbacView = useRbacView();
  const query = useQuery();
  const editable = useRbacEdit();

  const showInitial = query.get('initial') === '';
  const showFarms = query.get('farms') === '';
  const showOrders = query.get('orders') === '';
  const showCreditBureau = query.get('credit-bureau') === '';
  const showHistory = query.get('history') === '';
  const showSalesforce = query.get('salesforce') === '';
  const showAddresses = query.get('addresses') === '' || query.get('addresses') === 'new';
  const withNewAddress = query.get('addresses') === 'new';
  const showSurvey = query.get('survey') === '';

  const [_, setIsCompany] = useState<boolean>(false);
  const [profileProgress, setProfileProgress] = useState<IProfileProgress | null>(null);

  const [questions, setQuestions] = useState<SurveyQuestion[]>([]);
  const [savedQuestions, setSavedQuestions] = useState<SurveyQuestion[]>([]);
  const [isFixedSurvey, setIsFixedSurvey] = useState(false);
  const [answeredDate, setAnsweredDate] = useState<Date | null>(null);

  const deepCopyQuestion = (q: SurveyQuestion, index: number) => ({
    ...q,
    position: index + 1,
    rows: q.rows ? q.rows.map(r => ({
      ...r,
      cols: r.cols ? r.cols.map(c => ({ ...c })) : [],
    })) : [],
    answerIds: q.answerIds ? [...q.answerIds] : [],
    answers: q.answers ? [...q.answers] : [],
  });

  const getSurvey = async (preserveLocalChanges = false) => {
    if (!user) return;

    const {
      surveyQuestions, answeredDate: loadedAnsweredDate, isFixed,
    } = await surveyHttp.getUserSurvey(user.id);

    if (preserveLocalChanges) {
      setQuestions(prevQuestions =>
        surveyQuestions.map((serverQ, index) => {
          const localQ = prevQuestions.find(q => q.id === serverQ.id);
          if (serverQ.userQuestionId === null && localQ &&
              (localQ.rows.length > 0 || localQ.answerIds.length > 0 || localQ.customAnswer)) {
            return { ...localQ, position: index + 1 };
          }
          return deepCopyQuestion(serverQ, index);
        }),
      );
      setSavedQuestions(surveyQuestions.map((q, index) => deepCopyQuestion(q, index)));
    } else {
      setQuestions(surveyQuestions.map((q, index) =>
        deepCopyQuestion(q, index),
      ));
      setSavedQuestions(surveyQuestions.map((q, index) =>
        deepCopyQuestion(q, index),
      ));
    }

    setAnsweredDate(loadedAnsweredDate);
  };

  const onSaveQuestions = async (questions: SurveyQuestion[]) => {
    if (isFixedSurvey) {
      return;
    }
    if (user?.id && questions.length) {
      await Promise.all(
        questions.map(q => {
          let type: UpdateCreditSurveyType;

          if (q.isPlanner) {
            type = 'answer-percent';
          } else if (q.isTable) {
            type = 'answer-table';
          } else {
            type = 'answer';
          }

          const params = q.isTable ? {
            rows: q.rows.map((r: AnswerRow): CreditSurveyTableRowProps => ({
              cols: r.cols.map((c: AnswerCol): CreditSurveyTableColProps => ({
                answerId: c.answerId,
                customAnswer: c.customAnswer,
              })),
            })),
          } : q.isPlanner ? {
            rows: q.rows.map((r: AnswerRow): CreditSurveyTableRowProps => ({
              cols: r.cols.map((c: AnswerCol): CreditSurveyTableColProps => ({
                answerId: c.answerId,
                customAnswer: c.customAnswer,
              })),
            })),
          } : { answerIds: q.answerIds, customAnswer: q.customAnswer || undefined };

          return surveyHttp.updateUserSurvey({
            userId: user.id,
            questionId: q.id,
            params,
            type,
          });
        }),
      ).then(async () => {
        notification.success({ message: 'Survey is saved', duration: 8 });
        await getSurvey(true);
      }).catch((e) => {
        console.error(e);
        const errorMessage = axiosErrorToString(e);
        notification.error({ message: errorMessage, duration: 8 });
      });
    }
  };

  useEffect(() => {
    getSurvey();
  }, [user?.id]);

  useEffect(() => {
    if (record) {
      setIsCompany(!!user?.isCompany);
      (async () => {
        const res = await userHttp.getProfileProgress(record.id);
        setProfileProgress(res);
      })();
    }
  }, [record]);

  if (!rbacView) {
    return <Forbidden />;
  }

  const rfcSymbols = 13;
  const rfcCompanySymbols = 12;

  const validateRfcField = async (fieldName: 'rfc' | 'rfcCompany', value: string): Promise<string | undefined> => {
    const res = await userHttp.checkRfc({
      userId: user?.id,
      isCompany: fieldName === 'rfcCompany',
      rfc: value,
    });

    if (!res.rfcIsFree) {
      const text = 'RFC already in use';
      if (res.userCodes.length) {
        return text + ` by ${res.userCodes.join(', ')}`;
      }
      return text;
    }

    return undefined;
  };

  const validateUserCreation = async (values: any) => {
    const errors: any = {};
    const firstNameLength = (values.firstName || '').length;
    const lastNameLength = (values.lastName || '').length;
    const middleNameLength = (values.middleName || '').length;
    const rfcLength = (values.rfc || '').length;
    const rfcCompanyLength = (values.rfcCompany || '').length;
    const curpLength = (values.curp || '').length;

    if ((values.password || '').length > 0) {
      // eslint-disable-next-line
      const passwordCheck = /((?=.*[0-9])(?=.*[A-z])(?=.*\d).{8,})/;
      if (!passwordCheck.test(values.password)) {
        errors.password =
          'The password must contain at\u00a0least 1\u00a0letter,' +
          ' 1\u00a0number and length more than 8\u00a0symbols.';
      } else if (values.confirmPassword !== values.password) {
        errors.confirmPassword = 'Confirm password should be the same as a password.';
      }
    }

    if (values.rfc) {
      if (rfcLength !== rfcSymbols) {
        errors.rfc = `RFC should be ${rfcSymbols} characters`;
      } else {
        const err = await validateRfcField('rfc', values.rfc);
        if (err) {
          errors.rfc = err;
        }
      }
    }
    if (values.rfcCompany) {
      if (rfcCompanyLength !== rfcCompanySymbols) {
        errors.rfcCompany = `RFC (Company) should be ${rfcCompanySymbols} characters`;
      } else {
        const err = await validateRfcField('rfcCompany', values.rfcCompany);
        if (err) {
          errors.rfcCompany = err;
        }
      }
    }

    if (firstNameLength < 3 || firstNameLength > 30) {
      errors.firstName = 'The field must contain 3-30 symbols';
    }
    if (lastNameLength < 3 || lastNameLength > 30) {
      errors.lastName = 'The field must contain 3-30 symbols';
    }
    if (middleNameLength && (middleNameLength < 3 || lastNameLength > 30)) {
      errors.middleName = 'The field must contain 3-30 symbols';
    }
    if (curpLength > 0 && curpLength < 18) {
      errors.curp = 'CURP should be 18 character';
    }

    return errors;
  };

  const EditActions = () => {
    return editable ? (
      <Toolbar className={cls.toolbar}>
        <DeleteButton />
        <ReminderComponent user={user}/>
        <SaveButton label="Save" type="button" />
      </Toolbar>
    ) : null;
  };

  if (showInitial) {
    return user ? (
      <Edit {...props} title={<Title />} {...editProps}>
        <UserInitial user={user} />
      </Edit>
    ) : <Loader />;
  }

  if (showFarms) {
    return user ? (
      <UserFarmsPage user={user} />
    ) : <Loader />;
  }

  if (showOrders) {
    return user ? (
      <Edit {...props} title={<Title />} {...editProps}>
        <UserOrdersList user={user} />
      </Edit>
    ) : <Loader />;
  }

  if (showCreditBureau) {
    return user ? (
      <Edit {...props} title={<Title />} {...editProps}>
        <UserCreditBureauPage user={user} />
      </Edit>
    ) : <Loader />;
  }

  if (showHistory) {
    return user ? (
      <Edit {...props} title={<Title />} {...editProps}>
        <UserHistoryPage user={user} />
      </Edit>
    ) : <Loader />;
  }

  if (showSalesforce) {
    return user ? (
      <Edit {...props} title={<Title />} {...editProps}>
        <UserSalesforcePage user={user} />
      </Edit>
    ) : <Loader />;
  }

  if (showAddresses) {
    return user ? (
      <Edit {...props} title={<Title />} {...editProps}>
        <UserAddressesList user={user} withNewAddress={withNewAddress} />
      </Edit>
    ) : <Loader />;
  }

  if (showSurvey) {
    return user ? (
      <Edit {...props} title={<Title />} {...editProps}>
        <UserSurveyDetail
          userId={user.id}
          isFixedSurvey={isFixedSurvey}
          questions={questions}
          savedQuestions={savedQuestions}
          setQuestions={setQuestions}
          answeredDate={answeredDate}
          onUpdate={onSaveQuestions}
        />
      </Edit>
    ) : <Loader />;
  }

  const backTo = `/user/${user?.id}?initial`;

  return (
    <InitialPage title="Identity" backTo={backTo} backText="Back to Individual User">
      <Edit {...props} title={<Title />} {...editProps}>
        <SimpleForm
          mode="onChange"
          reValidateMode="onChange"
          toolbar={<EditActions />}
          validate={validateUserCreation}>
          <UserInfo user={user} profileProgress={profileProgress} />
          <UserAddresses user={user} />
          <UserPassword user={user} />
          <UserComments user={user} editable={editable} />
        </SimpleForm>
      </Edit>
    </InitialPage>
  );
};
